import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CURRENT_SEASON } from '@/lib/season';

export interface LeagueMatchdaySelection {
  user_id: string;
  display_name: string;
  team_name: string | null;
  used_double_down: boolean;
  points_earned: number | null;
  match_result: 'win' | 'draw' | 'loss' | null;
  processed: boolean;
  has_selection: boolean;
  is_autopick: boolean;
  is_forfeit: boolean;
}

/**
 * Hook to fetch all league members' selections for a specific matchday.
 * Returns all league members, with their selection data if they've made one.
 * 
 * League members can see all selections at any time (before and after kickoff).
 */
export const useLeagueMatchdaySelections = (
  leagueId: string | undefined,
  matchday: number | undefined,
  season: string = CURRENT_SEASON
) => {
  return useQuery({
    queryKey: ['league-matchday-selections', leagueId, matchday, season],
    queryFn: async (): Promise<LeagueMatchdaySelection[]> => {
      if (!leagueId || !matchday) return [];

      // Fetch all active league members
      const { data: members, error: membersError } = await supabase
        .from('league_memberships')
        .select('user_id, display_name')
        .eq('league_id', leagueId)
        .eq('status', 'active');

      if (membersError) {
        console.error('Error fetching league members:', membersError);
        throw membersError;
      }

      if (!members || members.length === 0) {
        return [];
      }

      // Fetch selections for this matchday and league
      // RLS policy allows league members to view all selections anytime
      const { data: selections, error: selectionsError } = await supabase
        .from('user_selections')
        .select('user_id, team_name, used_double_down, points_earned, match_result, processed, autopicked_at, is_forfeit')
        .eq('league_id', leagueId)
        .eq('matchday', matchday)
        .eq('season', season);

      if (selectionsError) {
        console.error('Error fetching matchday selections:', selectionsError);
        throw selectionsError;
      }

      // Create a map of selections by user_id
      type SelectionRow = { user_id: string; team_name: string; used_double_down: boolean; points_earned: number | null; match_result: string | null; processed: boolean; autopicked_at: string | null; is_forfeit: boolean | null };
      const selectionsByUserId = new Map(
        ((selections || []) as SelectionRow[]).map(s => [s.user_id, s])
      );

      // Map members to include their selection data
      const result: LeagueMatchdaySelection[] = members.map(member => {
        const selection = selectionsByUserId.get(member.user_id);
        return {
          user_id: member.user_id,
          display_name: member.display_name,
          team_name: selection?.team_name ?? null,
          used_double_down: selection?.used_double_down ?? false,
          points_earned: selection?.points_earned ?? null,
          match_result: selection?.match_result as 'win' | 'draw' | 'loss' | null ?? null,
          processed: selection?.processed ?? false,
          has_selection: !!selection,
          is_autopick: !!selection?.autopicked_at,
          is_forfeit: !!selection?.is_forfeit,
        };
      });

      // Sort: users with selections first, then alphabetically by display name
      result.sort((a, b) => {
        if (a.has_selection !== b.has_selection) {
          return a.has_selection ? -1 : 1;
        }
        return a.display_name.localeCompare(b.display_name);
      });

      return result;
    },
    enabled: !!leagueId && !!matchday,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
};
